import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from '@atlaskit/spinner';
import styled from '@emotion/styled';
import EmailIcon from '@atlaskit/icon/glyph/email';
import { colors, gridSize } from '@atlaskit/theme';

import FieldGroup from '../../../components/FieldGroup';
import EmailContainer, { FieldViewType } from '../../../components/EmailContainer';
import PasswordContainer from '../../../components/PasswordContainer';
import TallButton from '../../../components/TallButton';
import FormError from '../../../components/FormError';
import FormInfo from '../../../components/FormInfo';
import { StyledSocialLoginButton } from '../../../components/SocialLoginPanel';
import messages from './LoginForm.messages';
import { LoginFieldError, LoginGlobalInfo, CheckUserNameRedirectType } from '../../../types';
import { CoreApplication, applicationsMetadata } from '../../../utilities/applications';
import { useAnalyticsClient } from '../../../utilities/analytics/analytics-web-client';
import { loginPageId } from '../constants';
import { LoginRecaptcha } from './LoginRecaptcha';
import { useLoginTooManyAttemptsRecaptchaContext } from '../contexts/LoginTooManyAttemptsRecaptchaContext';
import { LoginMarketingConsent } from './MarketingConsent';
import { LoginGlobalErrorWithMessage } from '../hooks/useLogin';
import { useTranslateFormErrorIfAvailable } from '../../signup/FormValidation.messages';
import { Checkbox } from '@atlaskit/checkbox';
import { Box, Flex } from '@atlaskit/primitives';
import InlineMessage from '@atlaskit/inline-message';
import FedrampUseIdProbe from '../../../utilities/debug/FedrampUseIdProbe';
import { isFedM } from '../../../utilities/env';

export const OpeningSso = styled.div`
  align-items: center;
  color: ${colors.N90};
  display: flex;
  height: 40px;
  justify-content: space-between;
  padding: 0 8px;
`;

export const Form = styled.form`
  margin: 0;
`;

const InputsWrapper = styled.div`
  margin-bottom: ${gridSize()}px;
`;

interface ValidationErrors {
  email?: string;
  password?: string;
}

const getEmailFieldViewType = (restrictedEmail, redirectType, isSecondStep, isLoading) => {
  if (restrictedEmail || redirectType === CheckUserNameRedirectType.Sso) {
    return FieldViewType.Restricted;
  } else if (isSecondStep || isLoading) {
    return FieldViewType.ReadOnlyWithEditButton;
  }
  return FieldViewType.Editable;
};

export type LoginFormProps = {
  application: CoreApplication | null;
  redirectType: CheckUserNameRedirectType | null;
  isRedirecting?: boolean;
  isSecondStep?: boolean;
  isPasswordlessAllowed?: boolean;
  isLoading?: boolean;
  isCheckUsernameLoading?: boolean;
  onContinue: (username: string) => void;
  onLogIn: (email: string, password: string) => void;
  onPasswordlessLogIn: (email: string) => void;
  fieldErrors: LoginFieldError;
  email: string;
  error: LoginGlobalErrorWithMessage | null | undefined;
  info: LoginGlobalInfo | null | undefined;
  onEditEmail: () => void;
  onValidationError: (errors: ValidationErrors) => void;
  onFormClick: () => void;
  restrictedEmail: boolean;
  pageId: string;
  onEmailChange: (username: string) => void;
  showRememberMeCheckbox: boolean;
  rememberMeChecked: boolean;
  onToggleRememberMeCheckbox: () => void;
  onRedirectToSingleSignOn?: () => void;
};

export const LoginForm = ({
  application,
  email,
  error,
  fieldErrors,
  info,
  isLoading,
  isPasswordlessAllowed,
  isRedirecting,
  isSecondStep,
  isCheckUsernameLoading,
  onContinue,
  onEditEmail,
  onEmailChange,
  onFormClick,
  onLogIn,
  onPasswordlessLogIn,
  onValidationError,
  pageId,
  redirectType,
  restrictedEmail,
  showRememberMeCheckbox,
  rememberMeChecked,
  onToggleRememberMeCheckbox,
  onRedirectToSingleSignOn,
}: LoginFormProps) => {
  const intl = useIntl();
  const translatedEmailFieldError = useTranslateFormErrorIfAvailable(fieldErrors.email);
  const analyticsClient = useAnalyticsClient();
  const [password, setPassword] = useState<string>('');
  const [buttonClicked, setButtonClicked] = useState<'submit' | 'passwordless'>('submit');
  const inputRef = useRef<HTMLInputElement>(null);
  const [passwordFieldError, setPasswordFieldError] = useState<string | null>(null);

  const onEmailClick = useCallback(() => {
    setPassword('');
    setPasswordFieldError(null);
    onEditEmail();
  }, [onEditEmail]);

  const { captchaValue, isRequired: isTooManyAttemptsRecaptchaRequired } =
    useLoginTooManyAttemptsRecaptchaContext();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const emailEmpty = trimmedEmail === '';
    const emailInvalid = !trimmedEmail.match(/.+@.+/);
    const passwordEmpty = password === '';

    // Ignore submission of form while the app is loading.
    if (isLoading) {
      return;
    }

    setButtonClicked('submit');
    analyticsClient.buttonClickedEvent(pageId, isSecondStep ? 'login' : 'continueToLogin');

    if (emailEmpty) {
      inputRef.current?.focus();
      const emailValidationError = formatMessage(messages.emailEmpty);
      analyticsClient.errorShownEvent(loginPageId, 'validationError');
      onValidationError({ email: emailValidationError });
      return;
    }

    if (emailInvalid) {
      inputRef.current?.focus();
      const emailValidationError = formatMessage(messages.emailInvalid);
      analyticsClient.errorShownEvent(loginPageId, 'validationError');
      onValidationError({ email: emailValidationError });
      return;
    }

    if (isSecondStep && passwordEmpty) {
      setPasswordFieldError(formatMessage(messages.passwordEmpty));
      return;
    }

    if (!isSecondStep) {
      onContinue(trimmedEmail);
    } else {
      onLogIn(trimmedEmail, password);
    }
  };

  const handlePasswordlessButtonClick = async () => {
    const trimmedEmail = email.trim();

    if (isLoading || !trimmedEmail) {
      return;
    }

    setButtonClicked('passwordless');
    onPasswordlessLogIn(trimmedEmail);
  };

  const handlePasswordChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>) => {
      if (passwordFieldError) {
        setPasswordFieldError(null);
      }

      setPassword(e.currentTarget.value);
    },
    [passwordFieldError, setPasswordFieldError, setPassword]
  );

  const buttonContent = () => {
    if (!isSecondStep) {
      return <FormattedMessage id="login.form.continue" defaultMessage="Continue" />;
    }
    if (isPasswordlessAllowed) {
      return (
        <FormattedMessage id="common.login.with.password" defaultMessage="Log in with password" />
      );
    }
    return <FormattedMessage id="common.login" defaultMessage="Log in" />;
  };

  const infoContent = () => {
    const { formatMessage } = intl;
    const applicationName = application ? applicationsMetadata[application].name : 'Product';
    const testId = 'login-info-test-id';

    if (info) {
      switch (info.code) {
        case 'migratingExistingUser': {
          const summary = formatMessage(messages.migrateAccountInfo, {
            application: applicationName,
          });
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        case 'existingUserSignupAttempt': {
          const summary = formatMessage(messages.existingUserSignupAttemptInfo);
          return (
            <FormInfo id="login-info" data-testid={testId}>
              {summary}
            </FormInfo>
          );
        }
        default:
          return (
            <FormInfo id="login-info" data-testid={testId} isHidden={true}>
              {' '}
            </FormInfo>
          );
      }
    }
  };

  useEffect(() => {
    analyticsClient.stopInitialLoadApdexEvent('viewLoginPage');
  }, [analyticsClient]);

  const emailError = (): string | undefined => {
    switch (fieldErrors.email) {
      case 'unknown':
        return intl.formatMessage(messages.unexpectedErrorHappened);
      case 'rate_limit_exceeded_message':
        return intl.formatMessage(messages.rateLimitErrorMessage);
      default:
        return translatedEmailFieldError;
    }
  };

  const renderExperimentButtons = () => {
    if (redirectType === CheckUserNameRedirectType.Sso) {
      return (
        <OpeningSso>
          <FormattedMessage
            id="login.form.opening.sso"
            defaultMessage="Opening your single sign-on provider"
          />
          <Spinner />
        </OpeningSso>
      );
    } else {
      return (
        <TallButton
          appearance="primary"
          id="login-submit"
          testId="login-submit-idf-testid"
          type="submit"
          isFullWidth
          isDisabled={isSecondStep && isTooManyAttemptsRecaptchaRequired && !captchaValue}
          isLoading={
            buttonClicked === 'submit' && (!!isRedirecting || isLoading || isCheckUsernameLoading)
          }>
          {buttonContent()}
        </TallButton>
      );
    }
  };

  const { formatMessage } = intl;

  return (
    <div>
      {isFedM() && <FedrampUseIdProbe context="login-form" />}
      {error && !isLoading && <FormError>{error?.message}</FormError>}
      <span>{infoContent()}</span>
      {onRedirectToSingleSignOn && !isSecondStep && (
        <Box paddingBlockEnd="space.200">
          <TallButton
            testId="login-single-sign-on-button-idf-testid"
            type="button"
            onClick={onRedirectToSingleSignOn}
            isFullWidth>
            <FormattedMessage
              id="login.form.button.redirect.sso"
              defaultMessage="Log in with single sign-on"
            />
          </TallButton>
        </Box>
      )}
      <Form
        id="form-login"
        data-testid="form-login"
        onSubmit={handleSubmit}
        onClick={onFormClick}
        noValidate>
        <InputsWrapper>
          <EmailContainer
            fieldViewType={getEmailFieldViewType(
              restrictedEmail,
              redirectType,
              isSecondStep,
              isLoading
            )}
            fieldError={emailError()}
            onChange={onEmailChange}
            value={email}
            onEmailClick={onEmailClick}
            placeholderOverride={formatMessage(messages.emailPlaceholder)}
            inputRef={inputRef}
          />
          <PasswordContainer
            isHidden={!isSecondStep}
            fieldError={passwordFieldError || undefined}
            onChange={handlePasswordChange}
            value={password}
            className={`field-group password-field ${isSecondStep ? 'expanded' : 'hidden'}`}
            isRequired={isSecondStep}
          />

          <LoginRecaptcha />
          <LoginMarketingConsent />

          {/* Don't render the checkbox during SSO sign in, since it's already in flight. */}
          {showRememberMeCheckbox && redirectType !== CheckUserNameRedirectType.Sso && (
            <Box paddingBlockStart="space.100" paddingBlockEnd="space.100">
              <Flex direction="row" gap="space.050" alignItems="center">
                <Checkbox
                  testId="remember-me-checkbox"
                  isChecked={rememberMeChecked}
                  onClick={onToggleRememberMeCheckbox}
                  name="remember"
                  label={<FormattedMessage {...messages.rememberMeCheckboxLabel} />}
                  // During the redirect process (and while loading), we want to disable the checkbox so that the user doesn't interact with the checkbox.
                  isDisabled={
                    buttonClicked === 'submit' &&
                    (!!isRedirecting || isLoading || isCheckUsernameLoading)
                  }
                />
                <InlineMessage
                  spacing="compact"
                  appearance="info"
                  iconLabel={formatMessage(messages.rememberMeMoreInfoLabel)}
                  placement="right"
                  fallbackPlacements={['bottom']}>
                  <FormattedMessage {...messages.rememberMeTooltip} />
                </InlineMessage>
              </Flex>
            </Box>
          )}
        </InputsWrapper>
        {renderExperimentButtons()}
        <FieldGroup isHidden={!isSecondStep || !isPasswordlessAllowed}>
          <StyledSocialLoginButton
            id="passwordless-button"
            iconBefore={<EmailIcon label="email" />}
            iconAfter={<span style={{ width: 24 }} />} // to center text
            isFullWidth={true}
            advisoryEnabled={false}
            onClick={handlePasswordlessButtonClick}
            isLoading={buttonClicked === 'passwordless' && (!!isRedirecting || isLoading)}
            isDisabled={
              (isTooManyAttemptsRecaptchaRequired && !captchaValue) || isLoading || isRedirecting
            }>
            <FormattedMessage
              id="login.form.passwordless.button"
              defaultMessage="Log in with email"
            />
          </StyledSocialLoginButton>
        </FieldGroup>
      </Form>
    </div>
  );
};

export default LoginForm;
